import { msg } from "@lingui/core/macro";
import { RefreshCw, RotateCcw } from "@fleet/icons";
import { type Component, createEffect, ErrorBoundary, type JSX } from "solid-js";

import { Button } from "@/components/ui/button";
import { BrokenLinkIllustration } from "@/components/ui/illustrations";
import { cn } from "@habits/shared";
import { useI18n } from "@/providers/i18n-provider";

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** Compact variant for inline/regional boundaries (no full-height centering). */
  variant?: "full" | "region";
  /** Region name, surfaced in the log to disambiguate where it failed. */
  region?: string;
}

/**
 * Premium, theme-aware fallback for Solid `<ErrorBoundary>`. Always paints
 * something legible (illustration + title + description + actions) so the user
 * never faces a blank screen. "Try again" resets the boundary (re-runs the
 * subtree); "Reload" hard-reloads as the escape hatch.
 */
const ErrorFallback: Component<ErrorFallbackProps> = (props) => {
  const { t } = useI18n();

  createEffect(() => {
    console.error(
      `[ErrorBoundary${props.region ? `:${props.region}` : ""}] Caught render error:`,
      props.error,
    );
  });

  const message = () =>
    props.error instanceof Error ? props.error.message : t(msg`An unexpected error has occurred.`);

  const isRegion = () => props.variant === "region";

  return (
    <div
      role="alert"
      data-testid="error-boundary-fallback"
      class={cn(
        "flex flex-col items-center justify-center text-center",
        isRegion() ? "gap-4 rounded-xl border border-border/40 bg-card/60 p-8" : "min-h-[60vh] p-6",
      )}
    >
      <div class="animate-scale-in">
        <BrokenLinkIllustration
          aria-hidden="true"
          class={cn("text-accent animate-float", isRegion() ? "h-24 w-24" : "h-32 w-32")}
        />
      </div>
      <div class="mt-4 space-y-2">
        <h2 class={cn("font-bold tracking-tight", isRegion() ? "text-lg" : "text-2xl")}>
          {t(msg`Something went wrong`)}
        </h2>
        <p class="mx-auto max-w-sm text-sm text-muted-foreground">{message()}</p>
      </div>
      <div class="mt-2 flex w-full max-w-xs flex-col gap-2 sm:flex-row">
        <Button
          class="flex-1 gap-2"
          data-testid="error-boundary-retry"
          onClick={() => {
            props.reset();
          }}
        >
          <RotateCcw class="h-4 w-4" />
          {t(msg`Try again`)}
        </Button>
        <Button
          variant="outline"
          class="flex-1 gap-2"
          onClick={() => {
            globalThis.location.reload();
          }}
        >
          <RefreshCw class="h-4 w-4" />
          {t(msg`Reload page`)}
        </Button>
      </div>
    </div>
  );
};

interface AppErrorBoundaryProps {
  children: JSX.Element;
  variant?: "full" | "region";
  region?: string;
}

/**
 * Convenience wrapper around Solid's `<ErrorBoundary>` with the premium
 * fallback. Use `variant="region"` for inline sections (e.g. Stats) and the
 * default `"full"` for the global app shell.
 */
export const AppErrorBoundary: Component<AppErrorBoundaryProps> = (props) => {
  // Render-prop fallback for Solid's <ErrorBoundary>. Returning <ErrorFallback/>
  // (a top-level component) keeps it a stable reference — not a nested component
  // definition.
  const renderFallback = (error: unknown, reset: () => void) =>
    ErrorFallback({
      error,
      reset,
      variant: props.variant ?? "full",
      region: props.region,
    });

  return <ErrorBoundary fallback={renderFallback}>{props.children}</ErrorBoundary>;
};
